import { inject, injectable } from 'inversify';
import SERVICE_IDENTIFIER from '../framework/src/constants/identifiers';
import SystemConfig, {
    CONFIG_PARAMS,
} from '../framework/src/system/SystemConfig';
import GameSlotsCore from '../framework/src/game/GameSlotsCore';
import ISlots from '../framework/src/game/slot/ISlots';
import { EASE, getTweenAsync, } from '../framework/src/modules/Tween';
import GAME_PARTS from './identifiers';

import * as PIXI from 'pixi.js-legacy';
import PixiLayers from '../framework/src/engine/pixi/PixiLayers';
import { Frames } from './Code/Frames';
import Board from './Animations/Board';
import Wallet from '../framework/src/wallet/Wallet';
import MadmaxPixiLayer from '../framework/src/engine/pixi/MadmaxPixiLayer';
import Commander from './Commander';
import Win from './Animations/Win';
import PixiAudio from '../framework/src/engine/pixi/PixiAudio';
import { getRandomInt } from '../framework/src/helpers';

@injectable()
export default class WinController {    
    private slots: ISlots;    

    commander: Commander;    
    config: SystemConfig;
    allWinLines: Array<Array<number>>;

    wallet: Wallet;

    darkenBackground: PIXI.Sprite; //PIXI.Graphics;    
    board: Board;
    frame: Frames;

    reelsLayer: MadmaxPixiLayer;
    animLayer: MadmaxPixiLayer;
    blackLayer: MadmaxPixiLayer;

    symbolLines: PIXI.Container;

    win: Win;
    audio : PixiAudio;    

    constructor(
        @inject(SERVICE_IDENTIFIER.Wallet) wallet: Wallet,
        @inject(SERVICE_IDENTIFIER.GameCore) core: GameSlotsCore,
        @inject(SERVICE_IDENTIFIER.EngineAppLayers) layers: PixiLayers,
        @inject(SERVICE_IDENTIFIER.SystemConfig) config: SystemConfig,
        @inject(SERVICE_IDENTIFIER.Commander) commander: Commander,
        @inject(SERVICE_IDENTIFIER.EngineAudio) audio: PixiAudio,        
        @inject(GAME_PARTS.Board) board: Board,
        @inject(GAME_PARTS.IdleWin) win: Win,        
    ) {
        this.wallet = wallet;
        this.slots = core.slots;
        
        this.reelsLayer = layers.getLayer("reels");       

        this.symbolLines = new PIXI.Container();
        this.symbolLines.name = "WinController Symbol Lines Cont";        
        layers.getLayer("symbolAnimations").addChild(this.symbolLines);
        
        this.config = config;
        this.audio = audio;
        
        this.board = board;        
        
        this.allWinLines = config.get(CONFIG_PARAMS.WIN_LINES);
        this.commander = commander;

        this.initDarkenBackground();
        layers.getLayer("black").addChild(this.darkenBackground);
        this.win = win;        
    }

    async run(lineWins: Array<any>, index: number) {
        
        let resSymbols = this.slots.getResultSymbols();  // get result symbols
        // console.log(resSymbols);
        this.board.turnLightsOff();  // clear the win lines table in case previous line is still there        
        
        this.turnOnDarkBackground();

        // show board
        await this.board.show(500);

        for (; index < lineWins.length; index++) {

            // if free spins ain't in the last position of lineWins , then this need to be fixed
            if (lineWins[index].type == 'freeSpins') {
                //await this.board.hide(250);
                await this.win.reset(this.win.backgroundImg);
                await this.returnChildren();
                return index;
            }

            // send line win to the method lineWin
            await this.lineWin(lineWins[index], resSymbols) 

            // return symbols to the reel layer after the win line animation is done
            await this.returnChildren();
            // turn the board lights off for the next win line
            this.board.turnLightsOff();            
        }

        if (this.commander.skipAnim){
            await this.win.reset(this.win.backgroundImg);           
        }
        
        await this.turnOffDarkBackground();
        await this.board.hide(200);//250);

        return index;
    }

    /*
     * return symbols back on the reels layer
     */
    async returnChildren() {
        
        while (this.symbolLines.children.length > 0) {                
            this.reelsLayer.addChild(this.symbolLines.children[0]); // this.animLayer.children[i]);            
        }

        if (this.symbolLines.children.length > 0) {
            for (let child in this.symbolLines.children) {
                console.warn("child > " + child + " value> " + this.symbolLines.children[child]);
            }
            console.error(" symbol animation layer still got children ");
        }        
    }

    /*     
     * go through the lineWins array, and run animation for each symbol in lineWin
     */ 
    async lineWin(lineWin: any, resultSymbols){
        // prepare array for promises
        let promises = [];
        let symbolList = [];
        let noSkip = false;
        promises.push(this.board.line(this.allWinLines[lineWin.line]));

        if (!this.commander.skipAnim){            
            promises.push(this.playSymbolAudio(lineWin.symbol, lineWin.type)); 
            noSkip = true;
        }
       
        for (let pos of lineWin.position) {
            let symbol = resultSymbols[pos[0] * 3 + pos[1]];
            symbolList.push(symbol);
            this.symbolLines.addChild(symbol);

            if (noSkip){//!this.commander.skipAnim){ -- what if false during loop               

                promises.push(await symbol.create(lineWin.multiplier));
                promises.push(symbol.play());
                if (lineWin.multiplier > 1) {
                    promises.push(symbol.multiplier.animate());
                }                
                
            }else{
                promises.push(this.audio.playTrack("misc_Coin_Drop_"+ getRandomInt(1,5)));
            }
        }
        
        promises.push(await this.win.play(this.wallet.moneyToCredit(lineWin.win),noSkip).then(()=>{ //this.commander.skipAnim).then(()=>{
            this.wallet.addWin(lineWin.win);
        }));

        await Promise.all(promises).then(async () => {
        
            for (let symb of symbolList) {                
                await symb.clear();                
            }
            promises.length = 0;
            symbolList.length = 0;
        });        
    }

    playSymbolAudio(symbolIndex : number, winLinetype:string){
        let symbolSound = "";

        switch (symbolIndex){
            case 4: symbolSound = "Bracelet"; break; // "symbol_Bracelet":
            case 5: symbolSound = "Shield"; break;   // "symbol_Shield":
            case 6: symbolSound = "Axe"; break;      // "symbol_Axe":
            case 7: symbolSound = "Helmet"; break;   // "symbol_Helmet":
            case 8: symbolSound = "Woman"; break;    // "symbol_Woman":
            case 9: symbolSound = "Viking"; break;   // "symbol_Viking":
            case 11: symbolSound = "Scatter"; break; // "symbol_Scatter":
            default:
                symbolSound = "Letter"; 
        }

        symbolSound = "win_"+ ( winLinetype == "wild" ? "Multiplier_" : "") + symbolSound;

        // if (!this.audio.isPlaying(symbolSound)){
            this.audio.playTrack(symbolSound);
        // }
    }

    async turnOnDarkBackground() {
        if (!this.darkenBackground.alpha) {
            await getTweenAsync( this.darkenBackground, { alpha: 0.7 }, 100, EASE.LINEAR ); // default was 1 this needs to be changed if using alternative approach 
        }
    }

    async turnOffDarkBackground() {
        await getTweenAsync( this.darkenBackground, { alpha: 0 }, 100, EASE.LINEAR );
    }

    initDarkenBackground() {
        /*
        this.darkenBackground = new PIXI.Graphics();
        this.darkenBackground.name = "WinController Darken Background";
        this.darkenBackground.beginFill(0x000000, 0.7);  // 0.5);
        this.darkenBackground.drawRect(0, 0, 1280, 720);
        this.darkenBackground.endFill();
        this.darkenBackground.alpha = 0;     
        */
        /* alternative way */
        this.darkenBackground = new PIXI.Sprite(PIXI.Texture.WHITE);
        this.darkenBackground.name = "WinController Darken Background";
        this.darkenBackground.width = 1280;
        this.darkenBackground.height = 720;
        this.darkenBackground.tint = 0x000000;
        this.darkenBackground.alpha = 0; // max alpha = 0.7;
        
        
    }
}
